/**
 * RFC-163 — Integration registry.
 *
 * Single source of truth for the onboarding Connect Stage tile grid and
 * the server-side connector list. One row per supported integration,
 * keyed by stable slug. Grouped by compliance function; UI renders
 * tiles in group order.
 *
 * "State" reflects what we can do with the integration today:
 *   - 'live'          → adapter + native checks exist; Connect + Scan work.
 *   - 'connect-only'  → adapter + connection flow exist; no native checks
 *                        mapped yet. Connection still provides policy grounding.
 *   - 'coming-soon'   → no adapter yet. Tile is inert with a Notify-me CTA.
 *
 * Adding a new integration is a PR to this file + an adapter + checks.
 * The Connect Stage UI consumes this directly; avoid hardcoding slugs
 * in components.
 */
import type { IntegrationType } from '../generated/enums';

export type IntegrationGroup =
  | 'cloud'
  | 'identity'
  | 'productivity'
  | 'source-control'
  | 'observability'
  | 'device-endpoint'
  | 'incident-response'
  | 'security-scanning';

export type IntegrationState =
  | 'live'
  | 'connect-only'
  | 'coming-soon'
  /**
   * Attest-only — we don't have an adapter and don't plan to add one
   * soon, but the vendor is in the catalog so the user can attest "I
   * use this" during onboarding. Tiles with this state never appear
   * in the Connect Stage tile grid; they only surface in the
   * Security Profile attestation cards.
   */
  | 'attest-only';

/**
 * RFC-172 Section 1 — every Section-1 vendor category corresponds
 * to a single ComplianceProgram column. Tagging registry entries
 * with this field lets the Security Profile UI build itself from
 * the registry instead of duplicating the vendor list.
 */
export type SecurityProfileField =
  | 'cloudProviders'
  | 'identityProviders'
  | 'codeRepositories'
  | 'databaseProviders'
  | 'mdmTools'
  | 'edrTools'
  | 'emailSecurityTools'
  | 'backupTools'
  | 'monitoringTools'
  | 'alertingTools'
  | 'siemTools'
  | 'socProvider'
  | 'vulnScanningTools'
  | 'dlpTools'
  | 'wafTools'
  | 'backgroundCheckProviders'
  | 'trainingPlatforms'
  | 'aiProviders'
  | 'communicationTools'
  | 'ticketingPlatforms';

export const SECURITY_PROFILE_FIELDS = [
  'cloudProviders',
  'identityProviders',
  'codeRepositories',
  'databaseProviders',
  'mdmTools',
  'edrTools',
  'emailSecurityTools',
  'backupTools',
  'monitoringTools',
  'alertingTools',
  'siemTools',
  'socProvider',
  'vulnScanningTools',
  'dlpTools',
  'wafTools',
  'backgroundCheckProviders',
  'trainingPlatforms',
  'aiProviders',
  'communicationTools',
  'ticketingPlatforms',
] as const satisfies readonly SecurityProfileField[];

export interface IntegrationRegistryEntry {
  /** Stable slug. Matches adapter folder name where possible. */
  slug: string;
  /** Display name shown on the tile. */
  displayName: string;
  /**
   * One line naming the COMPLIANCE VALUE this vendor provides — "Branch
   * protection, required reviews, Dependabot", not "Connect to GitHub to
   * import repositories".
   *
   * Deliberately distinct from the connect-card copy in
   * AVAILABLE_INTEGRATION_TYPES (common.router.ts), which answers a different
   * question: "what will connecting this do for me?". 33 of 34 linked tiles
   * carry different text on purpose. Naming this field `complianceValue`
   * rather than `description` keeps that divergence legible instead of
   * looking like drift.
   *
   * Rendered as the chip title in the readiness questionnaire
   * (readiness-questionnaire/suggested-checks.ts).
   */
  complianceValue: string;
  /** Function grouping for the tile grid. */
  group: IntegrationGroup;
  /** Current build state. */
  state: IntegrationState;
  /**
   * RFC-172 Section 1 — when set, this vendor surfaces as an attest
   * card under the named Security Profile category. The slug is the
   * value persisted in `ComplianceProgram[securityProfileField]`.
   * An array means the vendor belongs to multiple categories (e.g.
   * Slack as both alerting and communication); the user toggles each
   * separately and they write to different columns.
   * An explicit empty list opts out of category-derived attestation fields.
   */
  securityProfileField?: SecurityProfileField | SecurityProfileField[];
  /**
   * Adapter module name under `server/src/services/integrations/adapters/`.
   * Null for coming-soon entries. Some tiles reuse an adapter another
   * tile owns (e.g. Entra ID piggybacks on azure.adapter.ts).
   */
  adapterSlug: string | null;
  /**
   * Connection mechanism. `oauth` uses the popup+message flow already
   * in place; `credentials` uses the credentials form dialog.
   */
  connectionMethod: 'oauth' | 'credentials' | 'api-key' | 'unknown';
  /**
   * Integration type enum used by the existing `Integration` table.
   * Null for coming-soon entries (no row type yet).
   */
  integrationType: IntegrationType | null;
  /** Logo asset path served from web/public. */
  logoPath?: string;
  /** Number of native checks available for onboarding tile metadata. */
  checkCount?: number;
  /**
   * Optional highlight label rendered on the tile — "High value",
   * "Required for M365 shops", etc. Kept short (< 30 chars).
   */
  highlightLabel?: string;
}

export const INTEGRATION_REGISTRY: IntegrationRegistryEntry[] = [
  // ── Cloud ──────────────────────────────────────────────────────────
  {
    slug: 'aws',
    displayName: 'AWS',
    complianceValue: 'Scan IAM roles, S3 bucket policies, CloudTrail, security groups, and RDS encryption.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'aws',
    connectionMethod: 'credentials',
    integrationType: 'AWS_READONLY',
    logoPath: '/vendor-logos/aws.png',
    highlightLabel: 'High value',
  },
  {
    slug: 'azure',
    displayName: 'Azure',
    complianceValue: 'Scan role assignments, activity logs, and network security groups.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'azure',
    connectionMethod: 'oauth',
    integrationType: 'AZURE_READONLY',
    logoPath: '/vendor-logos/azure.png',
    highlightLabel: 'High value',
  },
  {
    slug: 'gcp',
    displayName: 'Google Cloud',
    complianceValue: 'Scan IAM bindings, Cloud Audit Logs, organization policies, and Google Cloud posture.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'gcp',
    connectionMethod: 'oauth',
    integrationType: 'GCP_READONLY',
    logoPath: '/vendor-logos/google-cloud.png',
  },
  {
    slug: 'kubernetes',
    displayName: 'Kubernetes',
    complianceValue: 'Scan API server, controller manager, etcd, kubelet, RBAC, and pod security configuration.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'kubernetes',
    connectionMethod: 'credentials',
    integrationType: 'Kubernetes',
    logoPath: '/vendor-logos/kubernetes.svg',
    checkCount: 10,
  },
  {
    slug: 'tencent',
    displayName: 'Tencent Cloud',
    complianceValue: 'Discover CAM access, CVM instances and CloudAudit configuration.',
    group: 'cloud', state: 'live', securityProfileField: 'cloudProviders',
    adapterSlug: 'tencent', connectionMethod: 'credentials', integrationType: 'TencentCloud',
    logoPath: '/vendor-logos/tencent.svg', checkCount: 3,
  },
  {
    slug: 'aliyun',
    displayName: 'Alibaba Cloud (Aliyun)',
    complianceValue: 'Discover RAM access, ECS instances and ActionTrail configuration. Check console MFA and audit trail settings.',
    group: 'cloud', state: 'live', securityProfileField: 'cloudProviders',
    adapterSlug: 'aliyun', connectionMethod: 'credentials', integrationType: 'Aliyun',
    logoPath: '/vendor-logos/aliyun.svg', checkCount: 3,
  },
  {
    slug: 'cloudflare',
    displayName: 'Cloudflare',
    complianceValue:
      'Scan zones for SSL/TLS strictness, DNSSEC, WAF coverage, and R2 bucket exposure. Audit account 2FA, member roles, and API token rotation.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'cloudflare',
    connectionMethod: 'credentials',
    integrationType: 'Cloudflare',
    logoPath: '/vendor-logos/cloudflare.svg',
    checkCount: 8,
  },

  // ── Identity ───────────────────────────────────────────────────────
  {
    slug: 'okta',
    displayName: 'Okta',
    complianceValue: 'MFA enforcement, privileged group membership, access reviews, offboarding events.',
    group: 'identity',
    state: 'live',
    securityProfileField: 'identityProviders',
    adapterSlug: 'okta',
    connectionMethod: 'credentials',
    integrationType: 'Okta',
    logoPath: '/vendor-logos/okta.svg',
    checkCount: 12,
    highlightLabel: 'High value',
  },
  {
    slug: 'google-workspace',
    displayName: 'Google Workspace',
    complianceValue: '2SV enforcement, admin roles, mailbox audit, Drive external sharing, Calendar settings.',
    group: 'identity',
    state: 'live',
    securityProfileField: 'identityProviders',
    adapterSlug: 'google-workspace',
    connectionMethod: 'oauth',
    integrationType: 'GoogleWorkspace',
    logoPath: '/vendor-logos/google-workspace.png',
    checkCount: 10,
  },
  {
    slug: 'auth0',
    displayName: 'Auth0',
    complianceValue: 'Tenant settings, MFA policies, application grants, log events.',
    group: 'identity',
    state: 'live',
    securityProfileField: 'identityProviders',
    adapterSlug: 'auth0',
    connectionMethod: 'credentials',
    integrationType: 'Auth0',
    logoPath: '/vendor-logos/auth0.svg',
    checkCount: 8,
  },
  {
    slug: 'jumpcloud',
    displayName: 'JumpCloud',
    complianceValue: 'User directory, MFA policies, device trust, SSO applications.',
    group: 'identity',
    state: 'live',
    securityProfileField: 'identityProviders',
    adapterSlug: 'jumpcloud',
    connectionMethod: 'credentials',
    integrationType: 'JumpCloud',
    logoPath: '/vendor-logos/jumpcloud.svg',
    checkCount: 6,
  },
  {
    slug: 'onelogin',
    displayName: 'OneLogin',
    complianceValue: 'Authentication factors, role assignments, privileged user inventory.',
    group: 'identity',
    state: 'live',
    securityProfileField: 'identityProviders',
    adapterSlug: 'onelogin',
    connectionMethod: 'credentials',
    integrationType: 'OneLogin',
    logoPath: '/vendor-logos/onelogin.svg',
    checkCount: 6,
  },

  // ── Productivity ───────────────────────────────────────────────────
  {
    slug: 'microsoft-365',
    displayName: 'Microsoft Entra ID',
    complianceValue: 'Conditional Access, MFA methods, privileged role assignments, guest access, app consent, password policy.',
    group: 'identity',
    state: 'live',
    securityProfileField: 'identityProviders',
    adapterSlug: 'm365',
    connectionMethod: 'oauth',
    integrationType: 'M365',
    logoPath: '/vendor-logos/microsoft_tenant.svg',
    checkCount: 27,
    highlightLabel: 'High value',
  },
  {
    slug: 'microsoft-sharepoint',
    displayName: 'Microsoft SharePoint',
    complianceValue: 'External sharing, OneDrive sync, and modern-authentication posture for SharePoint Online.',
    group: 'productivity',
    state: 'live',
    adapterSlug: 'm365',
    connectionMethod: 'oauth',
    integrationType: 'MicrosoftSharePoint',
    logoPath: '/vendor-logos/microsoft_sharepoint.svg',
    checkCount: 3,
  },
  {
    // Teams shares the Microsoft Entra ID (M365) connection for connection
    // status. Its retained TCM check definitions are dormant.
    slug: 'teams',
    displayName: 'Microsoft Teams',
    complianceValue: 'Microsoft Teams connection status through your Microsoft Entra ID connection.',
    group: 'productivity',
    state: 'live',
    securityProfileField: ['alertingTools', 'communicationTools'],
    adapterSlug: 'm365',
    connectionMethod: 'oauth',
    integrationType: 'MicrosoftTeams',
    logoPath: '/vendor-logos/microsoft_teams.svg',
  },

  // ── Source Control ─────────────────────────────────────────────────
  {
    slug: 'github',
    displayName: 'GitHub',
    complianceValue: 'Branch protection, required reviews, Dependabot, Actions config, CODEOWNERS.',
    group: 'source-control',
    state: 'live',
    securityProfileField: ['codeRepositories', 'ticketingPlatforms'],
    adapterSlug: 'github',
    connectionMethod: 'oauth',
    integrationType: 'GitHub',
    logoPath: '/vendor-logos/github.svg',
    highlightLabel: 'High value',
  },
  {
    slug: 'gitlab',
    displayName: 'GitLab',
    complianceValue: 'Protected branches, MR approvals, pipeline security checks.',
    group: 'source-control',
    state: 'live',
    securityProfileField: 'codeRepositories',
    adapterSlug: 'gitlab',
    connectionMethod: 'credentials',
    integrationType: 'GitLab',
    logoPath: '/vendor-logos/gitlab.svg',
    checkCount: 18,
  },
  {
    slug: 'bitbucket',
    displayName: 'Bitbucket',
    complianceValue: 'Branch permissions, merge checks, pipeline configuration.',
    group: 'source-control',
    state: 'live',
    securityProfileField: 'codeRepositories',
    adapterSlug: 'bitbucket',
    connectionMethod: 'credentials',
    integrationType: 'Bitbucket',
    logoPath: '/vendor-logos/bitbucket.svg',
    checkCount: 15,
  },
  {
    slug: 'azure-devops',
    displayName: 'Azure DevOps',
    complianceValue: 'Branch policies, pipeline gates, service connections, work items.',
    group: 'source-control',
    state: 'live',
    securityProfileField: 'codeRepositories',
    adapterSlug: 'azure-devops',
    connectionMethod: 'oauth',
    integrationType: 'AZURE_DEVOPS',
    logoPath: '/vendor-logos/azure_devops.svg',
    checkCount: 7,
  },

  // ── Observability ──────────────────────────────────────────────────
  {
    slug: 'datadog',
    displayName: 'Datadog',
    complianceValue: 'Monitoring coverage, alert routing, log retention settings.',
    group: 'observability',
    state: 'live',
    securityProfileField: 'monitoringTools',
    adapterSlug: 'datadog',
    connectionMethod: 'credentials',
    integrationType: 'Datadog',
    logoPath: '/vendor-logos/datadog.svg',
    checkCount: 6,
  },
  {
    slug: 'axiom',
    displayName: 'Axiom',
    complianceValue: 'Dataset retention, monitor coverage, and alert routing configuration.',
    group: 'observability',
    state: 'live',
    securityProfileField: 'monitoringTools',
    adapterSlug: 'axiom',
    connectionMethod: 'credentials',
    integrationType: 'Axiom',
    logoPath: '/vendor-logos/axiom.png',
    checkCount: 4,
  },
  {
    slug: 'slack',
    displayName: 'Slack',
    complianceValue: 'Security alerting channels, workspace access policies, audit log retention.',
    group: 'observability',
    state: 'connect-only',
    securityProfileField: ['alertingTools', 'communicationTools'],
    adapterSlug: 'slack',
    connectionMethod: 'oauth',
    integrationType: 'Slack',
    logoPath: '/vendor-logos/slack.svg',
  },
  {
    slug: 'sentry',
    displayName: 'Sentry',
    complianceValue: 'Member 2FA, organization roles, pending invitations, and org-level 2FA/SSO enforcement.',
    group: 'observability',
    state: 'live',
    securityProfileField: 'monitoringTools',
    adapterSlug: 'sentry',
    connectionMethod: 'api-key',
    integrationType: 'Sentry',
    logoPath: '/vendor-logos/sentry.svg',
    checkCount: 4,
  },

  // ── Device / Endpoint ──────────────────────────────────────────────
  {
    slug: 'crowdstrike',
    displayName: 'CrowdStrike Falcon',
    complianceValue: 'Endpoint enrollment coverage and policy hygiene.',
    group: 'device-endpoint',
    state: 'live',
    securityProfileField: 'edrTools',
    adapterSlug: 'crowdstrike',
    connectionMethod: 'credentials',
    integrationType: 'CrowdStrike',
    logoPath: '/vendor-logos/crowdstrike.png',
    checkCount: 6,
  },
  {
    slug: 'jamf',
    displayName: 'Jamf Pro',
    complianceValue: 'macOS device baseline, FileVault enforcement, configuration profiles.',
    group: 'device-endpoint',
    state: 'coming-soon',
    securityProfileField: 'mdmTools',
    adapterSlug: null,
    connectionMethod: 'credentials',
    integrationType: null,
    logoPath: '/vendor-logos/jamf.svg',
  },
  {
    slug: 'intune',
    displayName: 'Microsoft Intune',
    complianceValue: 'Windows/Android device baseline, Conditional Access tie-in, compliance policies.',
    group: 'device-endpoint',
    state: 'live',
    securityProfileField: 'mdmTools',
    // Intune rides the m365.adapter.ts surface — the
    // `m365.entra-intune-*` checks read enrollment + compliance policy
    // state. Same Integration row as the M365 tile.
    adapterSlug: 'm365',
    connectionMethod: 'oauth',
    integrationType: 'M365',
    logoPath: '/vendor-logos/microsoft_tenant.svg',
  },

  // ── Incident Response ──────────────────────────────────────────────
  {
    slug: 'incident-io',
    displayName: 'incident.io',
    complianceValue: 'Account access, incident records, timelines, follow-ups and postmortem evidence visible to the connected key.',
    group: 'incident-response',
    state: 'live',
    securityProfileField: 'alertingTools',
    adapterSlug: 'incident_io',
    connectionMethod: 'api-key',
    integrationType: 'IncidentIo',
    logoPath: '/vendor-logos/incident-io.png',
  },
  {
    slug: 'firehydrant',
    displayName: 'FireHydrant',
    complianceValue: 'Incident runbooks, MTTR tracking, retrospective templates.',
    group: 'incident-response',
    state: 'coming-soon',
    securityProfileField: 'alertingTools',
    adapterSlug: null,
    connectionMethod: 'oauth',
    integrationType: null,
    logoPath: '/vendor-logos/firehydrant.png',
  },

  // ── Security Scanning ──────────────────────────────────────────────
  {
    slug: 'aikido',
    displayName: 'Aikido Security',
    complianceValue: 'Connect your security platform. Risk Register import is paused.',
    group: 'security-scanning',
    state: 'connect-only',
    securityProfileField: 'vulnScanningTools',
    adapterSlug: 'aikido',
    connectionMethod: 'oauth',
    integrationType: 'Aikido',
    checkCount: 0,
  },
  {
    slug: 'snyk',
    displayName: 'Snyk',
    complianceValue: 'Dependency vulnerability inventory, license compliance, remediation SLAs.',
    group: 'security-scanning',
    state: 'live',
    securityProfileField: 'vulnScanningTools',
    adapterSlug: 'snyk',
    connectionMethod: 'credentials',
    integrationType: 'Snyk',
    logoPath: '/vendor-logos/snyk.svg',
    checkCount: 5,
  },
  {
    slug: 'wiz',
    displayName: 'Wiz',
    complianceValue: 'Connect Wiz cloud security. Risk Register import is paused.',
    group: 'security-scanning',
    // Connect-only: credentials remain manageable while Risk ingestion is paused.
    // No native checks are registered; catalog-parity.test.ts enforces this.
    state: 'connect-only',
    securityProfileField: 'vulnScanningTools',
    adapterSlug: 'wiz',
    connectionMethod: 'credentials',
    integrationType: 'Wiz',
    logoPath: '/vendor-logos/wiz.svg',
    highlightLabel: 'Cloud security',
  },
  {
    slug: 'orca',
    displayName: 'Orca Security',
    complianceValue: 'Connect Orca Security. Risk Register import is paused.',
    group: 'security-scanning',
    // Connect-only: credentials remain manageable while Risk ingestion is paused.
    // No native checks are registered; catalog-parity.test.ts enforces this.
    state: 'connect-only',
    securityProfileField: 'vulnScanningTools',
    adapterSlug: 'orca',
    connectionMethod: 'credentials',
    integrationType: 'Orca',
    logoPath: '/vendor-logos/orca.png',
    highlightLabel: 'Cloud security',
  },

  // ─────────────────────────────────────────────────────────────────
  // RFC-172 Section 1 — attest-only vendors. No adapter; surface in
  // the Security Profile catalog so users can attest "I use this"
  // even when Vera can't auto-collect evidence yet.
  // ─────────────────────────────────────────────────────────────────

  // ── Cloud (attest-only) ─────────────────────────────────────────
  // Promoted from attest-only: the REST API backs four access-review checks.
  {
    slug: 'vercel',
    displayName: 'Vercel',
    complianceValue:
      'Team roster with roles and provisioning origin, owner-count bounds, SAML SSO enforcement, and stale pending invitations.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'vercel',
    connectionMethod: 'api-key',
    integrationType: 'Vercel',
    logoPath: '/vendor-logos/vercel.svg',
    checkCount: 4,
  },
  attest('netlify', 'Netlify', 'Hosting + serverless for static and Jamstack sites.', 'cloud', 'cloudProviders', '/vendor-logos/netlify.svg'),
  attest('digitalocean', 'DigitalOcean', 'Cloud infrastructure: droplets, kubernetes, managed DBs.', 'cloud', 'cloudProviders', '/vendor-logos/digital_ocean.svg'),
  attest('linode', 'Akamai (Linode)', 'Cloud compute, kubernetes, and managed databases.', 'cloud', 'cloudProviders', '/vendor-logos/linode.png'),
  attest('fly', 'Fly.io', 'Edge compute platform with global Postgres.', 'cloud', 'cloudProviders', '/vendor-logos/fly-io.png'),
  // Promoted from attest-only, but INVENTORY ONLY: Render's public API exposes
  // no workspace members, roles, or readable 2FA/SSO enforcement state, so the
  // access-review checks its peers carry are not buildable here.
  {
    slug: 'render',
    displayName: 'Render',
    complianceValue:
      'System-generated inventory of deployed services with type, public URL and creation date.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'cloudProviders',
    adapterSlug: 'render',
    connectionMethod: 'api-key',
    integrationType: 'Render',
    logoPath: '/vendor-logos/render.svg',
    checkCount: 1,
  },
  attest('heroku', 'Heroku', 'Salesforce-owned PaaS for web apps and add-ons.', 'cloud', 'cloudProviders', '/vendor-logos/heroku.svg'),
  // Attest-only on purpose. Inngest's v2 REST API exposes environments, event
  // and signing keys, apps and runs — but NO members, roles, SSO state, or
  // audit log, so no access-review or auth-posture check is buildable. What
  // remains (key-rotation age, PROD/BRANCH env separation, job inventory) does
  // not justify a connector; neither Vanta nor Drata integrates it either.
  // Revisit if Inngest ships a members or audit-log endpoint.
  attest('inngest', 'Inngest', 'Durable background jobs, scheduled workflows, and step functions.', 'cloud', 'cloudProviders', '/vendor-logos/inngest.png'),

  // ── Identity (attest-only) ──────────────────────────────────────
  attest('ping', 'Ping Identity', 'Enterprise SSO, MFA, and identity governance.', 'identity', 'identityProviders', '/vendor-logos/ping.png'),
  attest('aws-iam-identity-center', 'AWS IAM Identity Center', 'Single sign-on across AWS accounts and SAML apps.', 'identity', 'identityProviders', '/vendor-logos/aws.png'),
  attest('self-managed-idp', 'Self-managed IdP', 'In-house directory (LDAP / homegrown) with no vendor IdP.', 'identity', 'identityProviders'),

  // ── Source Control (attest-only) ────────────────────────────────
  attest('codecommit', 'AWS CodeCommit', 'AWS-managed Git hosting (deprecated for new accounts).', 'source-control', 'codeRepositories', '/vendor-logos/aws.png'),
  attest('self-hosted-git', 'Self-hosted Git', 'On-prem Gitea / Gerrit / GitLab CE / similar.', 'source-control', 'codeRepositories'),

  // ── Database providers ──────────────────────────────────────────
  {
    slug: 'aws-rds',
    displayName: 'AWS RDS',
    complianceValue: 'Managed Postgres / MySQL / SQL Server / Oracle on AWS.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'databaseProviders',
    // AWS RDS rides the aws.adapter.ts surface — encryption, public-access,
    // and backup config are pulled via describeDBInstances. Same Integration
    // row as the AWS cloud tile.
    adapterSlug: 'aws',
    connectionMethod: 'credentials',
    integrationType: 'AWS_READONLY',
    logoPath: '/vendor-logos/aws.png',
  },
  {
    slug: 'cloud-sql',
    displayName: 'Google Cloud SQL',
    complianceValue: 'Managed Postgres / MySQL / SQL Server on GCP.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'databaseProviders',
    // Cloud SQL rides the gcp.adapter.ts surface — listCloudSQLInstances
    // drives the public-access and SSL checks. Same Integration row as
    // the GCP cloud tile.
    adapterSlug: 'gcp',
    connectionMethod: 'oauth',
    integrationType: 'GCP_READONLY',
    logoPath: '/vendor-logos/google-cloud.png',
  },
  {
    slug: 'azure-sql',
    displayName: 'Azure SQL',
    complianceValue: 'Managed SQL Server / Postgres / MySQL on Azure.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'databaseProviders',
    // Azure SQL rides the azure.adapter.ts surface — listSqlServers drives
    // the TDE and firewall-rule checks. Same Integration row as the Azure
    // cloud tile.
    adapterSlug: 'azure',
    connectionMethod: 'oauth',
    integrationType: 'AZURE_READONLY',
    logoPath: '/vendor-logos/azure.png',
  },
  {
    slug: 'supabase',
    displayName: 'Supabase',
    complianceValue:
      'Scan projects for row-level security gaps, SSL enforcement, PITR backups, database network restrictions, Postgres currency, and organization member MFA.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'databaseProviders',
    adapterSlug: 'supabase',
    connectionMethod: 'credentials',
    integrationType: 'Supabase',
    logoPath: '/vendor-logos/supabase.svg',
    checkCount: 13,
    highlightLabel: 'High value',
  },
  attest('mongodb-atlas', 'MongoDB Atlas', 'Managed MongoDB across AWS / GCP / Azure.', 'cloud', 'databaseProviders', '/vendor-logos/mongodb.svg'),
  attest('planetscale', 'PlanetScale', 'Managed MySQL with branching and zero-downtime migrations.', 'cloud', 'databaseProviders', '/vendor-logos/planetscale.png'),
  attest('cockroach', 'CockroachDB', 'Distributed SQL with horizontal scale and survivability.', 'cloud', 'databaseProviders', '/vendor-logos/cockroachdb.png'),
  attest('neon', 'Neon', 'Serverless Postgres with branching.', 'cloud', 'databaseProviders', '/vendor-logos/neon.svg'),
  attest('self-managed-db', 'Self-managed database', 'On-prem or self-hosted database servers.', 'cloud', 'databaseProviders'),

  // ── MDM (attest-only) ───────────────────────────────────────────
  attest('mosyle', 'Mosyle', 'Apple device management for business and education.', 'device-endpoint', 'mdmTools', '/vendor-logos/mosyle.png'),
  attest('hexnode', 'Hexnode', 'Cross-platform unified endpoint management.', 'device-endpoint', 'mdmTools', '/vendor-logos/hexnode.png'),
  attest('workspace-one', 'VMware Workspace ONE', 'Unified endpoint management with conditional access.', 'device-endpoint', 'mdmTools', '/vendor-logos/vmware.svg'),

  // ── EDR (attest-only) ───────────────────────────────────────────
  attest('sentinelone', 'SentinelOne', 'Endpoint detection, response, and threat hunting.', 'device-endpoint', 'edrTools', '/vendor-logos/sentinelone.png'),
  attest('defender', 'Microsoft Defender', 'Endpoint detection bundled with Microsoft 365 / Entra.', 'device-endpoint', 'edrTools', '/vendor-logos/microsoft_tenant.svg'),
  attest('sophos', 'Sophos', 'Endpoint and server protection with EDR.', 'device-endpoint', 'edrTools', '/vendor-logos/sophos.svg'),
  attest('huntress', 'Huntress', 'Managed EDR with 24/7 SOC for SMBs.', 'device-endpoint', 'edrTools', '/vendor-logos/huntress.png'),
  attest('carbon-black', 'Carbon Black (VMware)', 'Endpoint protection and threat hunting.', 'device-endpoint', 'edrTools', '/vendor-logos/vmware.svg'),

  // ── Email security ──────────────────────────────────────────────
  attest('defender-o365', 'Microsoft Defender for Office 365', 'Anti-phishing, anti-malware, and Safe Links for M365.', 'security-scanning', 'emailSecurityTools', '/vendor-logos/microsoft_tenant.svg'),
  attest('proofpoint', 'Proofpoint', 'Enterprise email security and phishing protection.', 'security-scanning', 'emailSecurityTools', '/vendor-logos/proofpoint.png'),
  attest('mimecast', 'Mimecast', 'Email security, archiving, and continuity.', 'security-scanning', 'emailSecurityTools', '/vendor-logos/mimecast.png'),
  attest('abnormal', 'Abnormal Security', 'AI-native email security against advanced phishing.', 'security-scanning', 'emailSecurityTools', '/vendor-logos/abnormal-security.png'),
  attest('gws-builtin', 'Google Workspace built-in', 'Gmail spam, phishing, and malware filtering.', 'security-scanning', 'emailSecurityTools', '/vendor-logos/google-workspace.png'),

  // ── Backup ──────────────────────────────────────────────────────
  attest('aws-backup', 'AWS Backup', 'Centralized backup across AWS services.', 'cloud', 'backupTools', '/vendor-logos/aws.png'),
  attest('azure-backup', 'Azure Backup', 'Backup-as-a-service for Azure workloads.', 'cloud', 'backupTools', '/vendor-logos/azure.png'),
  attest('gcp-backup', 'Google Cloud Backup', 'Backup and DR for Google Cloud workloads.', 'cloud', 'backupTools', '/vendor-logos/google-cloud.png'),
  attest('veeam', 'Veeam', 'Backup, replication, and DR for virtual + cloud workloads.', 'cloud', 'backupTools', '/vendor-logos/veeam.svg'),
  attest('rubrik', 'Rubrik', 'Cloud data management with immutable backups.', 'cloud', 'backupTools', '/vendor-logos/rubrik.png'),
  attest('druva', 'Druva', 'SaaS-native backup and DR.', 'cloud', 'backupTools', '/vendor-logos/druva.png'),
  attest('backblaze', 'Backblaze', 'Cloud backup and B2 object storage.', 'cloud', 'backupTools', '/vendor-logos/backblaze.png'),
  attest('native-snapshots', 'Native cloud snapshots', 'Built-in EBS / Azure / GCP snapshot policies only.', 'cloud', 'backupTools'),

  // ── Monitoring ──────────────────────────────────────────────────
  attest('cloudwatch', 'Amazon CloudWatch', 'AWS-native metrics, logs, and alarms.', 'observability', 'monitoringTools', '/vendor-logos/aws.png'),
  attest('new-relic', 'New Relic', 'Full-stack observability with APM and infra monitoring.', 'observability', 'monitoringTools', '/vendor-logos/new_relic.svg'),
  attest('grafana', 'Grafana / Prometheus', 'Open-source metrics + dashboards stack.', 'observability', 'monitoringTools', '/vendor-logos/grafana.svg'),
  attest('vercel-logs', 'Vercel logs', 'Built-in logs and analytics for Vercel deployments.', 'observability', 'monitoringTools', '/vendor-logos/vercel.svg'),
  attest('elk', 'ELK / Elastic Stack', 'Elasticsearch + Logstash + Kibana for log analytics.', 'observability', 'monitoringTools', '/vendor-logos/elastic.png'),

  // ── Alerting / On-call (attest-only) ────────────────────────────
  attest('opsgenie', 'Opsgenie', 'Atlassian on-call and alerting with escalation policies.', 'incident-response', 'alertingTools', '/vendor-logos/opsgenie.svg'),
  attest('datadog-alerts', 'Datadog alerts', 'Alerts from Datadog monitors and SLOs.', 'observability', 'alertingTools', '/vendor-logos/datadog.svg'),
  attest('splunk-oncall', 'Splunk On-Call', 'On-call scheduling and incident response (formerly VictorOps).', 'incident-response', 'alertingTools', '/vendor-logos/splunk.png'),

  // ── SIEM ────────────────────────────────────────────────────────
  attest('splunk', 'Splunk', 'Log aggregation, correlation, and security analytics.', 'observability', 'siemTools', '/vendor-logos/splunk.png'),
  attest('sentinel', 'Microsoft Sentinel', 'Cloud-native SIEM and SOAR for Azure environments.', 'observability', 'siemTools', '/vendor-logos/microsoft_tenant.svg'),
  attest('sumo', 'Sumo Logic', 'Cloud-native log analytics and security monitoring.', 'observability', 'siemTools', '/vendor-logos/sumo_logic.svg'),
  attest('elastic-siem', 'Elastic SIEM', 'Security analytics on the Elastic Stack.', 'observability', 'siemTools', '/vendor-logos/elastic.png'),
  attest('datadog-siem', 'Datadog Cloud SIEM', 'SIEM built on Datadog log management.', 'observability', 'siemTools', '/vendor-logos/datadog.svg'),
  attest('panther', 'Panther', 'Detection-as-code SIEM built for cloud-native teams.', 'observability', 'siemTools', '/vendor-logos/panther-labs.png'),

  // ── SOC / MDR provider (single-select) ─────────────────────────
  attest('internal-24x7', 'Internal 24×7 SOC', 'In-house security operations team with round-the-clock coverage.', 'incident-response', 'socProvider'),
  attest('arctic-wolf', 'Arctic Wolf', 'Managed detection and response with concierge security team.', 'incident-response', 'socProvider', '/vendor-logos/arctic-wolf.png'),
  attest('expel', 'Expel', 'Managed detection and response across cloud and endpoints.', 'incident-response', 'socProvider', '/vendor-logos/expel.png'),
  attest('deepwatch', 'Deepwatch', 'Managed security platform with squad-based delivery.', 'incident-response', 'socProvider', '/vendor-logos/deepwatch.png'),
  attest('ontinue', 'Ontinue', 'Microsoft-native managed extended detection and response.', 'incident-response', 'socProvider', '/vendor-logos/ontinue.png'),
  attest('red-canary', 'Red Canary', 'Managed detection and response with extensive threat intel.', 'incident-response', 'socProvider', '/vendor-logos/red-canary.png'),
  attest('huntress-mdr', 'Huntress MDR', 'Managed EDR + ITDR with 24/7 SOC for SMBs.', 'incident-response', 'socProvider', '/vendor-logos/huntress.png'),
  attest('business-hours-only', 'Business hours only', 'Internal coverage during business hours; no 24/7 SOC.', 'incident-response', 'socProvider'),
  attest('soc-none', 'No SOC / MDR', 'No dedicated security operations coverage today.', 'incident-response', 'socProvider'),

  // ── Vulnerability scanning ──────────────────────────────────────
  {
    slug: 'dependabot',
    displayName: 'Dependabot',
    complianceValue: 'Automated dependency updates and vulnerability alerts (GitHub-native).',
    group: 'security-scanning',
    state: 'live',
    securityProfileField: 'vulnScanningTools',
    // Dependabot rides the github.adapter.ts surface — alerts are pulled
    // via `/repos/{owner}/{repo}/dependabot/alerts`. Connecting Dependabot
    // creates / reuses the same GitHub Integration row.
    adapterSlug: 'github',
    connectionMethod: 'oauth',
    integrationType: 'GitHub',
    logoPath: '/vendor-logos/github.svg',
  },
  attest('trivy', 'Trivy', 'Open-source container and IaC vulnerability scanner.', 'security-scanning', 'vulnScanningTools', '/vendor-logos/trivy.png'),
  {
    slug: 'ghas',
    displayName: 'GitHub Advanced Security',
    complianceValue: 'CodeQL, secret scanning, and dependency review for GitHub.',
    group: 'security-scanning',
    state: 'live',
    securityProfileField: 'vulnScanningTools',
    // GHAS rides the github.adapter.ts surface — secret-scanning alerts
    // are pulled via `/repos/{owner}/{repo}/secret-scanning/alerts`.
    // Connecting GHAS creates / reuses the same GitHub Integration row.
    adapterSlug: 'github',
    connectionMethod: 'oauth',
    integrationType: 'GitHub',
    logoPath: '/vendor-logos/github.svg',
  },
  attest('qualys', 'Qualys', 'Vulnerability management across endpoints and cloud.', 'security-scanning', 'vulnScanningTools', '/vendor-logos/qualys.svg'),
  attest('tenable', 'Tenable', 'Nessus-based vulnerability management and scanning.', 'security-scanning', 'vulnScanningTools', '/vendor-logos/tenable.svg'),
  attest('rapid7', 'Rapid7', 'InsightVM / InsightAppSec vulnerability management.', 'security-scanning', 'vulnScanningTools', '/vendor-logos/rapid7.png'),

  // ── DLP ─────────────────────────────────────────────────────────
  attest('purview', 'Microsoft Purview', 'Information protection and DLP across Microsoft 365.', 'security-scanning', 'dlpTools', '/vendor-logos/microsoft_tenant.svg'),
  attest('forcepoint', 'Forcepoint', 'Enterprise DLP across endpoints, cloud, and email.', 'security-scanning', 'dlpTools', '/vendor-logos/forcepoint.png'),
  attest('symantec', 'Symantec DLP (Broadcom)', 'Enterprise data loss prevention suite.', 'security-scanning', 'dlpTools', '/vendor-logos/symantec.svg'),
  attest('nightfall', 'Nightfall', 'AI-native DLP for SaaS and cloud environments.', 'security-scanning', 'dlpTools', '/vendor-logos/nightfall.png'),
  attest('netskope', 'Netskope', 'Cloud-native DLP, CASB, and SASE.', 'security-scanning', 'dlpTools', '/vendor-logos/netskope.png'),

  // ── WAF ─────────────────────────────────────────────────────────
  {
    slug: 'aws-waf',
    displayName: 'AWS WAF',
    complianceValue: 'Web application firewall on AWS.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'wafTools',
    // AWS WAF rides the aws.adapter.ts surface — global + regional Web
    // ACLs are pulled via the `aws:waf:global-webacls` and
    // `aws:wafv2:regional-webacls` checks. Same Integration row as the
    // AWS cloud tile.
    adapterSlug: 'aws',
    connectionMethod: 'credentials',
    integrationType: 'AWS_READONLY',
    logoPath: '/vendor-logos/aws.png',
  },
  {
    slug: 'cloudflare-waf',
    displayName: 'Cloudflare WAF',
    complianceValue: 'Edge web application firewall and bot management. Checks WAF and managed rulesets are enabled on every active zone.',
    group: 'cloud',
    state: 'live',
    securityProfileField: 'wafTools',
    // Cloudflare WAF rides the cloudflare.adapter.ts surface — WAF coverage
    // per zone is pulled via the `cloudflare.zone-waf-enabled` check. Same
    // Integration row as the Cloudflare cloud tile.
    adapterSlug: 'cloudflare',
    connectionMethod: 'credentials',
    integrationType: 'Cloudflare',
    logoPath: '/vendor-logos/cloudflare.svg',
  },
  attest('azure-frontdoor', 'Azure Front Door', 'Global WAF and CDN for Azure workloads.', 'cloud', 'wafTools', '/vendor-logos/azure.png'),
  attest('akamai', 'Akamai', 'Edge WAF, DDoS, and bot protection.', 'cloud', 'wafTools', '/vendor-logos/akamai.png'),
  attest('imperva', 'Imperva', 'WAF and application security with bot management.', 'cloud', 'wafTools', '/vendor-logos/imperva.png'),

  {
    slug: 'pagerduty',
    displayName: 'PagerDuty',
    complianceValue: 'Escalation policies, on-call staffing and schedule coverage, and incident closure history.',
    group: 'incident-response',
    state: 'live',
    securityProfileField: 'alertingTools',
    adapterSlug: 'pagerduty',
    connectionMethod: 'api-key',
    integrationType: 'PagerDuty',
    logoPath: '/vendor-logos/pagerduty.svg',
    checkCount: 4,
  },

  {
    slug: 'kandji',
    displayName: 'Kandji',
    complianceValue: 'Apple device roster, FileVault, application firewall, hardening-parameter audit, and MDM enrolment for remote wipe.',
    group: 'device-endpoint',
    state: 'live',
    securityProfileField: 'mdmTools',
    adapterSlug: 'kandji',
    connectionMethod: 'api-key',
    integrationType: 'Kandji',
    logoPath: '/vendor-logos/kandji.png',
    checkCount: 5,
  },

  // ── HRIS / people ops ───────────────────────────────────────────────
  // These five are adapter-backed and check-bearing but were absent from the
  // registry, so their tiles rendered without a logo and they were invisible
  // to the enterprise integrations API, setup-assessment, slot-provider-hints
  // and suggested-checks. `catalog-parity.test.ts` now prevents that drift.
  {
    slug: 'adp',
    displayName: 'ADP Workforce Now',
    complianceValue: 'Authorized personnel roster, employment dates, departments and managers.',
    group: 'productivity',
    securityProfileField: [],
    state: 'connect-only',
    adapterSlug: 'adp',
    connectionMethod: 'oauth',
    integrationType: 'ADP',
    logoPath: '/vendor-logos/adp.svg',
  },
  {
    slug: 'hibob',
    displayName: 'HiBob',
    complianceValue: 'Authorized personnel roster, employment status and dates, departments and managers.',
    group: 'productivity',
    state: 'live',
    checkCount: 1,
    adapterSlug: 'hibob',
    connectionMethod: 'oauth',
    integrationType: 'HiBob',
    logoPath: '/vendor-logos/hibob.svg',
  },
  {
    slug: 'successfactors', displayName: 'SAP SuccessFactors',
    complianceValue: 'Authorized personnel, work email and explicit employment departures.',
    group: 'productivity', state: 'connect-only', adapterSlug: 'successfactors',
    connectionMethod: 'oauth', integrationType: 'SuccessFactors', securityProfileField: [], logoPath: '/vendor-logos/sap_successfactors.svg',
  },
  {
    slug: 'bamboohr',
    displayName: 'BambooHR',
    complianceValue: 'Employee roster, termination dates, manager assignment, and security-training completion.',
    group: 'productivity',
    state: 'live',
    adapterSlug: 'bamboohr',
    connectionMethod: 'api-key',
    integrationType: 'BambooHR',
    logoPath: '/vendor-logos/bamboohr.svg',
    checkCount: 13,
  },
  {
    slug: 'gusto',
    displayName: 'Gusto',
    complianceValue: 'Employee roster, offboarding timeliness, manager and department assignment.',
    group: 'productivity',
    state: 'live',
    adapterSlug: 'gusto',
    connectionMethod: 'oauth',
    integrationType: 'Gusto',
    logoPath: '/vendor-logos/gusto.svg',
    checkCount: 10,
  },
  {
    slug: 'rippling',
    displayName: 'Rippling',
    complianceValue: 'Employee roster, offboarding, and org-structure completeness.',
    group: 'productivity',
    state: 'live',
    adapterSlug: 'rippling',
    connectionMethod: 'api-key',
    integrationType: 'Rippling',
    logoPath: '/vendor-logos/rippling.svg',
    checkCount: 9,
  },
  {
    slug: 'deel',
    displayName: 'Deel',
    complianceValue: 'Worker roster across contractors and EOR, offboarding, and contract status.',
    group: 'productivity',
    state: 'live',
    adapterSlug: 'deel',
    connectionMethod: 'api-key',
    integrationType: 'Deel',
    logoPath: '/vendor-logos/deel.svg',
    checkCount: 8,
  },
  {
    slug: 'quickbooks',
    displayName: 'QuickBooks',
    complianceValue: 'Period-close controls: closing date, post-close transaction edits, and idle sign-out.',
    group: 'productivity',
    state: 'live',
    adapterSlug: 'quickbooks',
    connectionMethod: 'oauth',
    integrationType: 'QuickBooks',
    logoPath: '/vendor-logos/quickbooks.svg',
    checkCount: 3,
  },

  // ── Background check provider ───────────────────────────────────
  { slug: 'checkr', displayName: 'Checkr', complianceValue: 'Background screening reports and dashboard account access.',
    group: 'productivity', state: 'live', adapterSlug: 'checkr', connectionMethod: 'oauth', integrationType: 'Checkr',
    logoPath: '/vendor-logos/checkr.png', checkCount: 9, securityProfileField: 'backgroundCheckProviders' },
  attest('goodhire', 'GoodHire', 'Background checks for SMB hiring teams.', 'productivity', 'backgroundCheckProviders', '/vendor-logos/goodhire.png'),
  attest('sterling', 'Sterling', 'Enterprise background screening and identity verification.', 'productivity', 'backgroundCheckProviders', '/vendor-logos/sterling.png'),
  attest('certn', 'Certn', 'Global background checks with continuous monitoring.', 'productivity', 'backgroundCheckProviders', '/vendor-logos/certn.png'),
  attest('hireright', 'HireRight', 'Enterprise background checks and onboarding screening.', 'productivity', 'backgroundCheckProviders', '/vendor-logos/hireright.png'),
  attest('manual-bg', 'Manual / in-house', 'Reference checks performed by the hiring team directly.', 'productivity', 'backgroundCheckProviders'),

  // ── Security awareness training ─────────────────────────────────
  // Promoted from attest-only: the Reporting API backs three native checks.
  // Stays in the trainingPlatforms security-profile field so the attestation
  // card and the connector describe the same thing.
  {
    slug: 'knowbe4',
    displayName: 'KnowBe4',
    complianceValue:
      'Security-awareness training completion joined against your employee roster, campaign recency, and new-hire auto-enrollment.',
    group: 'productivity',
    state: 'live',
    securityProfileField: 'trainingPlatforms',
    adapterSlug: 'knowbe4',
    connectionMethod: 'api-key',
    integrationType: 'KnowBe4',
    logoPath: '/vendor-logos/knowbe4.png',
    checkCount: 3,
  },
  attest('hoxhunt', 'Hoxhunt', 'Adaptive phishing training and reporting.', 'productivity', 'trainingPlatforms', '/vendor-logos/hoxhunt.png'),
  attest('curricula', 'Curricula (Huntress)', 'Story-based security awareness training.', 'productivity', 'trainingPlatforms', '/vendor-logos/huntress.png'),
  attest('proofpoint-awareness', 'Proofpoint Security Awareness', 'Awareness training tied to Proofpoint email security.', 'productivity', 'trainingPlatforms', '/vendor-logos/proofpoint.png'),
  attest('sans', 'SANS Security Awareness', 'SANS-authored security awareness training.', 'productivity', 'trainingPlatforms', '/vendor-logos/sans.png'),
  attest('internal-training', 'Internal training', 'Training built and delivered in-house.', 'productivity', 'trainingPlatforms'),

  // ── AI providers ────────────────────────────────────────────────
  attest('openai', 'OpenAI', 'GPT-class models via API or ChatGPT Enterprise.', 'productivity', 'aiProviders', '/vendor-logos/openai.svg'),
  attest('anthropic', 'Anthropic', 'Claude models via API or Claude Enterprise.', 'productivity', 'aiProviders', '/vendor-logos/anthropic.png'),
  attest('google-ai', 'Google AI', 'Gemini models via Google Cloud / AI Studio.', 'productivity', 'aiProviders', '/vendor-logos/google.png'),
  attest('fireworks', 'Fireworks AI', 'Open-source model hosting and serving.', 'productivity', 'aiProviders', '/vendor-logos/fireworks-ai.png'),
  attest('cohere', 'Cohere', 'LLM API for enterprise NLP applications.', 'productivity', 'aiProviders', '/vendor-logos/cohere.png'),

  // ── Communication ──────────────────────────────────────────────
  attest('email-only', 'Email only', 'No real-time messaging tool — email is the channel of record.', 'productivity', 'communicationTools'),
  {
    slug: 'zoom', displayName: 'Zoom',
    complianceValue: 'Account users, common roles, groups and authentication settings for evidence review.',
    group: 'productivity', state: 'live', securityProfileField: 'communicationTools', adapterSlug: 'zoom',
    connectionMethod: 'oauth', integrationType: 'Zoom', logoPath: '/vendor-logos/zoom.svg',
  },

  // ── Ticketing platform ─────────────────────────────────────────
  {
    slug: 'linear',
    displayName: 'Linear',
    complianceValue: 'Issue tracking for product engineering teams.',
    group: 'productivity',
    state: 'connect-only',
    securityProfileField: 'ticketingPlatforms',
    adapterSlug: 'linear',
    connectionMethod: 'oauth',
    integrationType: 'Linear',
    logoPath: '/vendor-logos/linear.svg',
  },
  {
    slug: 'jira',
    displayName: 'Jira',
    complianceValue: 'Atlassian issue tracking and project management.',
    group: 'productivity',
    state: 'connect-only',
    securityProfileField: 'ticketingPlatforms',
    adapterSlug: 'jira',
    connectionMethod: 'oauth',
    integrationType: 'Jira',
    logoPath: '/vendor-logos/jira.svg',
  },
  {
    slug: 'asana', displayName: 'Asana',
    complianceValue: 'Read-only tasks and available history from selected projects.',
    group: 'productivity', state: 'connect-only', securityProfileField: 'ticketingPlatforms',
    adapterSlug: 'asana', connectionMethod: 'oauth', integrationType: 'Asana',
    logoPath: '/vendor-logos/asana.svg',
  },
  {
    slug: 'dropbox', displayName: 'Dropbox',
    complianceValue: 'Business team members, reported roles, direct groups, sharing settings and 30-day security/admin activity for evidence review. Effective privilege, MFA and SSO require separate evidence.',
    group: 'productivity', state: 'live', adapterSlug: 'dropbox', connectionMethod: 'oauth', integrationType: 'Dropbox',
    logoPath: '/vendor-logos/dropbox.svg',
  },
  {
    slug: 'hubspot', displayName: 'HubSpot',
    complianceValue: 'Users, permission sets, teams and security/login history for compliance evidence review.',
    group: 'productivity', state: 'live', adapterSlug: 'hubspot', connectionMethod: 'oauth', integrationType: 'HubSpot',
    logoPath: '/vendor-logos/hubspot.svg',
  },
  {
    slug: 'salesforce', displayName: 'Salesforce',
    complianceValue: 'Read-only visible users, profiles, permission sets and assignments; effective access and authentication protection require review.',
    group: 'productivity', state: 'live', adapterSlug: 'salesforce', connectionMethod: 'oauth', integrationType: 'Salesforce',
    logoPath: '/vendor-logos/salesforce.svg',
  },
  {
    slug: 'snowflake', displayName: 'Snowflake',
    complianceValue: 'Account access inventory, 30-day login history and visible authentication settings. Account Usage can lag two hours; effective enforcement needs review.',
    group: 'cloud', state: 'live', securityProfileField: 'databaseProviders', adapterSlug: 'snowflake', connectionMethod: 'oauth', integrationType: 'Snowflake',
    logoPath: '/vendor-logos/snowflake.svg',
  },
  {
    slug: 'monday', displayName: 'Monday.com',
    complianceValue: 'Visible accounts, roles and direct team memberships for access reviews; effective permissions and authentication protection require review.',
    group: 'productivity', state: 'live', securityProfileField: [], adapterSlug: 'monday', connectionMethod: 'oauth', integrationType: 'Monday',
    logoPath: '/vendor-logos/monday.svg',
  },
  {
    slug: 'notion', displayName: 'Notion',
    complianceValue: 'Workspace people and bots for access reviews. Guests, roles and authentication enforcement require separate evidence.',
    group: 'productivity', state: 'live', securityProfileField: [], adapterSlug: 'notion', connectionMethod: 'oauth', integrationType: 'Notion',
    logoPath: '/vendor-logos/notion.svg',
  },
  {
    slug: 'confluence', displayName: 'Confluence',
    complianceValue: 'Visible site groups and direct members for access reviews. Groupless users, content permissions and authentication enforcement require separate evidence.',
    group: 'productivity', state: 'live', securityProfileField: [], adapterSlug: 'confluence', connectionMethod: 'oauth', integrationType: 'Confluence',
    logoPath: '/vendor-logos/confluence.svg',
  },
  {
    slug: 'box', displayName: 'Box',
    complianceValue: 'Enterprise access inventory, selected file sharing, collaboration domains and exemptions, and 30-day audit history for evidence review.',
    group: 'productivity', state: 'live', adapterSlug: 'box', connectionMethod: 'oauth', integrationType: 'Box',
    logoPath: '/vendor-logos/box.svg',
  },
  {
    slug: 'servicenow', displayName: 'ServiceNow',
    complianceValue: 'Access inventories and read-only incidents, changes, journals and current approvals from selected assignment groups.',
    group: 'productivity', state: 'live', securityProfileField: 'ticketingPlatforms',
    adapterSlug: 'servicenow', connectionMethod: 'oauth', integrationType: 'ServiceNow',
    logoPath: '/vendor-logos/servicenow.svg',
  },
  { slug: 'wrike', displayName: 'Wrike', complianceValue: 'Supporting account access inventory and selected-folder remediation tasks, regular comments and approval snapshots.',
    group: 'productivity', state: 'live', securityProfileField: 'ticketingPlatforms', adapterSlug: 'wrike',
    connectionMethod: 'oauth', integrationType: 'Wrike', logoPath: '/vendor-logos/wrike.ico' },
  { slug: 'trello', displayName: 'Trello', complianceValue: 'Supporting access inventory and selected-board remediation cards with available history.',
    group: 'productivity', state: 'live', securityProfileField: 'ticketingPlatforms', adapterSlug: 'trello',
    connectionMethod: 'oauth', integrationType: 'Trello', logoPath: '/vendor-logos/trello.svg' },
  { slug: 'shortcut', displayName: 'Shortcut', complianceValue: 'Workspace access and selected-team remediation stories with available history.',
    group: 'productivity', state: 'live', securityProfileField: 'ticketingPlatforms', adapterSlug: 'shortcut',
    connectionMethod: 'api-key', integrationType: 'Shortcut', logoPath: '/vendor-logos/shortcut.svg' },
  { slug: 'clickup', displayName: 'ClickUp', complianceValue: 'Workspace access and read-only tasks/comments from selected lists.',
    group: 'productivity', state: 'connect-only', securityProfileField: 'ticketingPlatforms',
    adapterSlug: 'clickup', connectionMethod: 'oauth', integrationType: 'ClickUp', logoPath: '/vendor-logos/clickup.svg' },
];

/**
 * Helper for attest-only registry rows. They share a uniform shape
 * (no adapter, no integration type, no connection method) so the
 * declaration list above stays compact.
 */
function attest(
  slug: string,
  displayName: string,
  complianceValue: string,
  group: IntegrationGroup,
  securityProfileField: SecurityProfileField | SecurityProfileField[],
  logoPath?: string,
): IntegrationRegistryEntry {
  return {
    slug,
    displayName,
    complianceValue,
    group,
    state: 'attest-only',
    securityProfileField,
    adapterSlug: null,
    connectionMethod: 'unknown',
    integrationType: null,
    logoPath,
  };
}

export const INTEGRATION_GROUP_LABELS: Record<IntegrationGroup, string> = {
  cloud: 'Cloud',
  identity: 'Identity',
  productivity: 'Productivity suite',
  'source-control': 'Source control',
  observability: 'Observability',
  'device-endpoint': 'Device / Endpoint',
  'incident-response': 'Incident response',
  'security-scanning': 'Security scanning',
};

/** Group-ordered for deterministic rendering. */
export const INTEGRATION_GROUP_ORDER: IntegrationGroup[] = [
  'cloud',
  'identity',
  'productivity',
  'source-control',
  'observability',
  'device-endpoint',
  'incident-response',
  'security-scanning',
];

// Indexed lookups built once at import time. The registry is small but
// these are read inside React render loops (per-tile), so the linear
// scans were O(tiles × registry).
const REGISTRY_BY_SLUG = new Map(
  INTEGRATION_REGISTRY.map((entry) => [entry.slug, entry] as const),
);
const REGISTRY_BY_TYPE = new Map<string, IntegrationRegistryEntry>(
  INTEGRATION_REGISTRY
    .filter((entry): entry is IntegrationRegistryEntry & { integrationType: IntegrationType } => entry.integrationType != null)
    .map((entry) => [entry.integrationType, entry] as const),
);
const REGISTRY_BY_GROUP = INTEGRATION_GROUP_ORDER.reduce<Record<IntegrationGroup, IntegrationRegistryEntry[]>>(
  (acc, group) => {
    acc[group] = INTEGRATION_REGISTRY.filter((entry) => entry.group === group);
    return acc;
  },
  {} as Record<IntegrationGroup, IntegrationRegistryEntry[]>,
);

export function getIntegrationBySlug(
  slug: string,
): IntegrationRegistryEntry | undefined {
  return REGISTRY_BY_SLUG.get(slug);
}

export function getIntegrationByType(
  integrationType: string,
): IntegrationRegistryEntry | undefined {
  return REGISTRY_BY_TYPE.get(integrationType);
}

/**
 * IntegrationType → slug of its PRIMARY registry entry.
 *
 * Several IntegrationTypes own more than one registry row (AWS_READONLY → aws /
 * aws-rds / aws-waf, M365 → microsoft-365 / intune), so the last-wins
 * `getIntegrationByType` can return a sub-entry rather than the canonical
 * "vendor you connected". Any consumer that needs the primary entry must go
 * through `getPrimaryIntegrationByType`, which honours this map first.
 */
export const PRIMARY_REGISTRY_SLUG_BY_TYPE: Partial<Record<IntegrationType, string>> = {
  AWS_READONLY: 'aws',
  GCP_READONLY: 'gcp',
  GCP_SECURITY_COMMAND_CENTER: 'gcp',
  AZURE_READONLY: 'azure',
  AZURE_DEFENDER: 'azure',
  Kubernetes: 'kubernetes',
  Cloudflare: 'cloudflare',
  Aliyun: 'aliyun',
  TencentCloud: 'tencent',
  M365: 'microsoft-365',
  MicrosoftSharePoint: 'microsoft-sharepoint',
  GitHub: 'github',
  GitLab: 'gitlab',
  Bitbucket: 'bitbucket',
  AZURE_DEVOPS: 'azure-devops',
  Okta: 'okta',
  GoogleWorkspace: 'google-workspace',
  Auth0: 'auth0',
  OneLogin: 'onelogin',
  JumpCloud: 'jumpcloud',
  Datadog: 'datadog',
  Axiom: 'axiom',
  CrowdStrike: 'crowdstrike',
  Snyk: 'snyk',
  Aikido: 'aikido',
  Wiz: 'wiz',
  Orca: 'orca',
  Slack: 'slack',
  MicrosoftTeams: 'teams',
  Zoom: 'zoom',
  Linear: 'linear',
  Asana: 'asana',
  ServiceNow: 'servicenow',
  Box: 'box',
  Snowflake: 'snowflake',
  Monday: 'monday',
  Shortcut: 'shortcut',
  Trello: 'trello',
  Wrike: 'wrike',
  Dropbox: 'dropbox',
  HubSpot: 'hubspot',
  Salesforce: 'salesforce',
  ClickUp: 'clickup',
  Jira: 'jira',
  MicrosoftIntune: 'intune',
  JamfPro: 'jamf',
  Checkr: 'checkr',
  SuccessFactors: 'successfactors',
  Sentry: 'sentry',
  PagerDuty: 'pagerduty',
  Kandji: 'kandji',
  KnowBe4: 'knowbe4',
  Vercel: 'vercel',
  Render: 'render',
};

/**
 * Canonical (primary) registry entry for an IntegrationType. Prefers the
 * explicit primary-slug map, then an entry whose slug matches its own
 * adapterSlug, then any entry tagged with the type. Returns undefined for
 * types the registry does not carry.
 */
export function getPrimaryIntegrationByType(
  integrationType: IntegrationType,
): IntegrationRegistryEntry | undefined {
  const preferredSlug = PRIMARY_REGISTRY_SLUG_BY_TYPE[integrationType];
  if (preferredSlug) return REGISTRY_BY_SLUG.get(preferredSlug);
  return (
    INTEGRATION_REGISTRY.find(
      (entry) => entry.integrationType === integrationType && entry.slug === entry.adapterSlug,
    ) ?? INTEGRATION_REGISTRY.find((entry) => entry.integrationType === integrationType)
  );
}

export function getIntegrationsByGroup(
  group: IntegrationGroup,
): IntegrationRegistryEntry[] {
  return REGISTRY_BY_GROUP[group] ?? [];
}

/** Live entries only — useful for coverage calculations. */
export function getLiveIntegrations(): IntegrationRegistryEntry[] {
  return INTEGRATION_REGISTRY.filter((entry) => entry.state === 'live');
}

/**
 * RFC-172 — every registry entry tagged with the given Security
 * Profile field. Includes connect-able vendors (state='live' or
 * 'connect-only') so users can attest "I use AWS" without going
 * through OAuth, and attest-only vendors that have no adapter.
 */
const REGISTRY_BY_SECURITY_PROFILE_FIELD: Map<SecurityProfileField, IntegrationRegistryEntry[]> = (() => {
  const map = new Map<SecurityProfileField, IntegrationRegistryEntry[]>();
  for (const entry of INTEGRATION_REGISTRY) {
    if (!entry.securityProfileField) continue;
    const fields = Array.isArray(entry.securityProfileField)
      ? entry.securityProfileField
      : [entry.securityProfileField];
    for (const field of fields) {
      const list = map.get(field) ?? [];
      list.push(entry);
      map.set(field, list);
    }
  }
  return map;
})();

export function getVendorsForSecurityProfileField(
  field: SecurityProfileField,
): IntegrationRegistryEntry[] {
  return REGISTRY_BY_SECURITY_PROFILE_FIELD.get(field) ?? [];
}
